import { TNCEntryInfo } from './api/b2ciam';
import { EnrollCardResponse } from './api/enroll-card';
import {
  CardBrand,
  CardType,
  MerchantConfig,
  PaymentInstrument,
  PurchaseIntentResponse,
  ThreeDSSetupOptions
} from './api/intent';
import { DpaData, DpaTransactionOptions, MobileNumber, PayloadTypeIndicator } from './emvco';
import { FetchResponseValue } from './fetch';
import { BooleanString, EmptyObject, WithoutVoid } from './utility';

export type FeatureToggles = {
  collectDebug?: boolean;
  enableLoaderRemoval?: boolean;
  debuggingEnabled?: boolean;
  /* START GENAI@GHCOPILOT */
  enableCofEnrollment?: boolean;
  /* END GENAI@GHCOPILOT */

  enabledSRCBankApp?: boolean;
  enabledSRCIsssuerOtpFlow?: boolean;
  // START GENAI@GHCOPILOT
  enableAlternativeAnalytics?: boolean;
  // END GENAI@GHCOPILOT
  enableIssuerAppAuth?: boolean;
  enableCpfSupportBrazil?: boolean;
  enableComboCardSupportBrazil?: boolean;
  //enableDaf feature toggle used to support shipping address in integrated flow for DAF enablement which is for risk decision
  enableNewStepupDesign?: boolean;
  enablePerformInlineIdentityValidation?: boolean;
  enableDaf?: boolean;
  enableDataDog?: boolean;
  enableLocaleCookie?: boolean;
  enabledIntegratedIssuerFlow?: boolean;
  enableIntegratedThreeDsDto?: boolean;
  enableKeepTabAlive?: boolean;
  enableLastUsedTs?: boolean;
  enableLogrocket?: boolean;
  enableLogrocketCollectHeaders?: boolean;
  enableLogrocketCollectPayload?: boolean;
  enableLogrocketForMerchant?: boolean;
  enableNonDcfCvv?: boolean;
  enableSkipMiCookie?: boolean;
  enableVpps?: boolean;
  enableMobileAsId?: boolean;
  fidoAuthEnabled?: boolean;
  isRecognizedDelayForThm?: boolean;
  orchestratorCheckoutApi?: boolean;
  orchestratorCheckoutEnrollApi?: boolean;
  orchestratorCheckoutEnrollCompliance?: boolean;
  orchestratorCheckoutEnrollRecognized?: boolean;
  orchestratorIdLookupApi?: boolean;
  orchestratorProbeApi?: boolean;
  orchestratorReissuanceApi?: boolean;
  orchestratorSsiEnrollApi?: boolean;
  recognizeNoVba?: boolean;
  sdkSplitSession?: boolean;
  stepupAppDomain?: boolean;
  stepupAppThreeds?: boolean;
  stepupInitialPopup?: boolean;
  uctpAddCard?: boolean;
  vbaDeviceProfile?: boolean;
  callbackBlockedFunction?: boolean;
};

export type LookupStatus = 'NOTPRESENT' | 'PRESENT' | 'USERNOTALLOWED';
export type CardNetwork = 'AMEX' | 'DISCOVER' | 'MASTERCARD' | 'VISA';

export type DCFActionCode =
  | 'ACCT_INACCESSIBLE'
  | 'ADD_CARD'
  | 'CANCEL'
  | 'CHANGE_CARD'
  | 'COMPLETE'
  | 'dcf:session-timeout:reset-app'
  | 'dcf:sign-out:change-user'
  | 'ERROR'
  | 'PENDING_AUTHENTICATION'
  | 'SWITCH_CONSUMER';

type SRCiRoute = 'add-card' | 'card-list' | 'identity' | 'otp' | 're-enter-card';
type DCFRoute = 'enroll-card' | 'review' | 'step-up';

export type ToRoute = DCFRoute | SRCiRoute;
export type CountryCode = string;
export type CurrencyCode = string;
export type Locale = string;
export type PublicKey = { e: number; n: string };
export type ReviewButtonAction = 'CONTINUE' | 'PAY';
export type UserType = 'erm' | 'ssi' | 'standard';
export type WidgetStyle = 'LIGHTBOX' | 'OVERLAY';
export type WOWOValue = 'INTERNAL_ONLY' | 'OFF' | 'ON';

export type CollectBillingAddressType =
  | 'CNTRY'
  | 'FULL'
  | 'NONE'
  | 'PARTIAL'
  | 'POSTALCNTRY'
  | undefined;

export type CookieAttributes = {
  correlation_id?: string;
  event: 'cookie attributes';
  cookie_banner_displayed: boolean;
  cookie_policy_accepted: boolean;
  can_drop_nonessential_cookies: boolean;
  xo_visitid?: string | null;
};

export type DCFLaunchFlow =
  | '/checkout-widget/vcop/dcf/tnc'
  | 'billingAddress'
  // dl-add-card is not a "deeplink", despite the prefix. This is what System
  // SDK passes after doing an add card flow in SRCi. The DCF should route to
  // the review screen.
  | 'dl-add-card'
  | 'enroll-card'
  | 'REVIEW'
  | 'review'
  | 'step-up';

export type SRCiLaunchFlow =
  | 'ACCEPT_NEW_TERMS'
  | 'account-locked'
  | 'ADD_ADDRESS'
  | 'ADD_CARD'
  | 'add-card'
  | 'ADDRESS_LIST'
  | 'card-list'
  | 'card-selector'
  | 'device-locked'
  | 'discovery'
  | 'EDIT_ADDRESS'
  | 'EDIT_CARD'
  | 'edit-card-delete'
  | 'ERM_SSI_STEPUP'
  | 'FORGOT_PASSWORD'
  | 'identity'
  | 'locked-card-list'
  | 'otp'
  | 're-enter-card'
  | 'stepup'
  | 'SWITCH_ADDRESS'
  | 'thank-you'
  | 'USE_PASSWORD'
  | 'USER_AUTHENTICATED'
  | 'WELCOME'
  | 'welcome';

export type LaunchFlow = DCFLaunchFlow | SRCiLaunchFlow;

export type ReviewObject = {
  buttonAction: ReviewButtonAction;
  message: string;
};

export type SDKCert = {
  kid: string;
  name?: string;
  publicKey: PublicKey;
};

export type SDKCerts = {
  [key in CardNetwork]: SDKCert | (Partial<SDKCert> & { error: string });
};

type SDKInfo = {
  apiKey: string;
  cardBrand: CardNetwork;
  domains: string[];
  panEncryptionKey: string;
  panEncryptionKid: string;
  sdkUri: string;
  sdkUriHash: string;
};

export type SRCInfo = {
  sdks: SDKInfo[];
};

type SRCNetworksMetadataLevel = 'country' | 'global' | 'merchant';

export type SRCNetworksMetadata = {
  level: SRCNetworksMetadataLevel;
  value: string; // comma-separated network names, e.g. 'VISA,AMEX', or 'ALL'
};

export type SRCNetworksFutMetadata = {
  enabled: BooleanString | boolean;
  level: SRCNetworksMetadataLevel;
};

export type TNCStatus = 'accepted' | 'not_accepted';

export type AddCardInfo = {
  cardSuffix?: string;
  enrollCardResponse?: EnrollCardResponse;
  firstName?: string;
  lastName?: string;
  networkId?: CardBrand;
  panJWE?: string;
};

export type AssuranceData = {
  verificationData?: {
    verificationEntity: string;
    verificationEvents?: string[];
    verificationMethod: string;
    verificationResults: string;
    verificationTimestamp: string;
    verificationType?: 'CARD' | 'CARDHOLDER' | 'CONSUMER' | 'DEVICE' | 'RELATIONSHIP';
  }[];
};

export type UserInfo = {
  // enum value indicating if account has password associated with it or not
  accountType: 'PASSWORD' | 'PASSWORDLESS';
  // true if user selects 'remember me' check box in otp screen.
  alwaysRememberMe: boolean;
  assuranceData: AssuranceData | EmptyObject | null;
  auth_level: string | undefined;
  browserLocale: Locale;
  // Array, follows SRC-I Adapter interface response format
  cardList: [];
  isCfeNewUser?: boolean;
  // response for identityLookup call, needed for GAMP user_login_status event
  consumerPresent: boolean | null;
  countryCode: CountryCode;
  customFlowType: 'paymentsetting' | 'withincheckout' | null;
  // user's email
  email: string;
  emailHash?: string | null;
  emailPromotions: boolean;
  // user's first name
  firstName: string;
  // is user erm, ssi, or neither
  flowType: UserType;
  // true if any of the Visa Checkout cookies are present - username, cardart, hasConsented, etc
  hasVCOCookie: boolean;
  isComplianceTncPassed: boolean;
  // indicates if users account/device is locked
  isLocked: boolean;
  // Existing user performed OTP VIA system-sdk prior to launching DCF
  isLoggedInOtpUser: boolean;
  isNonVisaRecognizedUser: boolean;
  isOptimizedHalfSheetEligible: boolean;
  isOTPLocked: boolean;
  isPrivacyPolicyPresent: boolean;
  // true (only happens once) when ssi/erm user is locked when landing on welcome screen
  isRecognizedLocked: boolean;
  // true if Visa has valid ssiEnabled/ermEnabled cookie
  isRecognizedUser: boolean;
  isTncFlow: boolean;
  // set final result of auth module
  isUserAuthenticated: boolean;
  // has user consented to using Merchant Prefill for first and last name on card entry form
  isUserConsentedPrefill: boolean;
  // is Vsb flow (V.initializeVsb called at least once)
  isVsb: boolean;
  // is Vsb buttonless flow (integrated flow) (V.initializeVsb is init latest call)
  isVsbButtonless: boolean;
  // user's last name
  lastName: string;
  locale: Locale;
  lookupStatus: LookupStatus | null;
  // masked username (email, phone, etc) returned by SRC initializeIdentityValidation call
  maskedValidationChannel: {
    otpEmail: string | null;
    otpPhone: string | null;
  };
  mobileNumber: MobileNumber;
  phone: string;
  signedOut?: boolean;
  // response from getSrcProfile call; contains card info for user from all networks (INO)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  srcProfile: any;
  tncStatus: TNCStatus;
  useBillingAsDelivery: boolean;
  // default set based on VCO cookies
  welcomeTabSelected: 0 | 1;
};

export type CheckoutFlow = 'SRC' | 'VDCP';
export type CheckoutFormFactor = 'hybridWeb' | 'other' | 'sdkLite' | 'web';
export type UXByFormFactor = Record<CheckoutFormFactor, CheckoutFlow | 'DISABLED'>;
export type DataLevel = 'FULL' | 'NONE' | 'SUMMARY';

export type InitialVInitRequest = {
  allowEXO?: boolean;
  allowRXO?: BooleanString;
  apikey: string;
  bannerDisplayName?: string;
  browserLocale?: Locale;
  countryCode?: CountryCode;
  customerSupportUrl?: string;
  dataLevel?: DataLevel;
  displayName?: string;
  enableUserDataPrefill?: boolean;
  externalClientId?: string;
  externalProfileId?: string;
  locale?: Locale;
  logoUrl?: string;
  parentUrl?: string;
  payment?: {
    acceptCanadianVISADebit?: boolean;
    acceptCanadianVisaDebit?: boolean;
    cardBrands?: CardBrand[];
    billingCountries?: CountryCode[];
  };
  paymentRequest?: PaymentRequest;
  promoCode?: string;
  referenceCallID?: string;
  review?: {
    message: string;
    buttonAction: ReviewButtonAction;
  };
  settings?: {
    bannerDisplayName?: string;
    countryCode?: CountryCode;
    customerSupportUrl?: string;
    dataLevel?: DataLevel;
    displayName?: string;
    locale?: Locale;
    logoUrl?: string;
    payment?: {
      acceptCanadianVISADebit?: boolean;
      acceptCanadianVisaDebit?: boolean;
      cardBrands?: CardBrand[];
      billingCountries?: CountryCode[];
    };
    review?: {
      message?: string;
      buttonAction?: ReviewButtonAction;
    };
    shipping?: {
      acceptedRegions?: CountryCode[];
      collectShipping?: BooleanString;
    };
    websiteUrl?: string;
    widgetStyle?: WidgetStyle;
  };
  shipping?: {
    acceptedRegions?: CountryCode[];
    collectShipping?: BooleanString | boolean;
  };
  sourceId?: string;
  websiteUrl?: string;
  widgetStyle?: WidgetStyle;
};

export type Payment = {
  billingCountries?: CountryCode[];
  collectBillingAddress?: CollectBillingAddressType;
};

export type PaymentRequest = {
  buttonAction: ReviewButtonAction;
  currencyCode?: CurrencyCode;
  orderId?: string;
  total?: number | string | null;
};

export type VInitRequest = {
  allowCustomBranding: BooleanString | boolean;
  allowEnrollment: BooleanString | boolean;
  allowEXO?: BooleanString;
  allowRXO?: BooleanString;
  allowSRC: BooleanString;
  allowSRCInterop: BooleanString;
  apikey: string;
  bannerDisplayName?: string;
  bannerURL: string;
  browserLocale: Locale;
  buttonAction: ReviewButtonAction;
  buttonPosition?: string;
  clientId: string;
  consumerNationalIdentifierRequested?: boolean;
  countryCode: CountryCode;
  currencyCode?: string;
  currencyFormat: string;
  customData?: {
    nvPair?: {
      name: string;
      value: string;
    }[];
  };
  customerSupportUrl: string;
  dataLevel?: DataLevel | PayloadTypeIndicator;
  displayName: string;
  enableThreeDS: boolean;
  enableUserDataPrefill: BooleanString | boolean;
  externalClientId: string;
  externalProfileId: string;
  formFactorMerchantOverride?: BooleanString | boolean;
  guestCheckout: BooleanString | boolean;
  intialVinit: InitialVInitRequest;
  locale?: Locale;
  logoUrl: string;
  merchantConfig?: MerchantConfig;
  orderId: string;
  orderType?: string;
  parentUrl: string;
  payment: Payment;
  paymentRequest: PaymentRequest;
  promoCode?: string;
  referenceCallID: string;
  review: ReviewObject;
  settings: {
    bannerDisplayName?: string;
    buttonPosition?: string;
    countryCode: CountryCode;
    customerSupportUrl: string;
    dataLevel?: DataLevel;
    displayName: string;
    locale: Locale;
    logoUrl: string;
    payment: {
      billingCountries?: CountryCode[];
    };
    review: {
      message: string;
      buttonAction: ReviewButtonAction;
    };
    shipping: {
      acceptedRegions?: CountryCode[];
      collectShipping: BooleanString;
    };
    websiteUrl: string;
    widgetStyle: WidgetStyle;
  };
  shipping: {
    acceptedRegions?: CountryCode[];
    collectShipping: BooleanString;
  };
  sourceId: string;
  srcBrandMerchantWhitelist?: string;
  srcBrandingOverride: 'allow' | 'disabled' | 'disallow';
  srcBrandingEnabledCountries?: string;
  srcBrandingWOWO: WOWOValue;
  srcDisabledCountries: string;
  srcInfo?: SRCInfo | null;
  srcInteropEnabledCountries: string;
  srcInteropExperimentCountries: string;
  srcInteropWOWO: WOWOValue;
  srcNetworks?: SRCNetworksMetadata;
  srcNetworksFut?: SRCNetworksFutMetadata;
  threeDSSetup?: ThreeDSSetupOptions;
  total?: number | string | null;
  transactionType?: string;
  uxByFormFactor: UXByFormFactor;
  websiteUrl: string;
  widgetStyle?: WidgetStyle;
};

export type SDKParams = {
  allow_enrollment: boolean;
  buttonCardBrands?: CardBrand[];
  button_type: string;
  card_art_source: string;
  channel: string;
  cobrand_displayed?: boolean;
  cobrand_support?: boolean;
  cookieAttributes: CookieAttributes;
  disable_svg_button: boolean;
  disable_svg_button_animation: boolean;
  external_client_id: string;
  flow: string;
  gtm: {
    allowCustomBranding: boolean | string;
    allowEnrollment: boolean;
    background_img_type?: 'default';
    collectShipping: BooleanString | boolean;
    currencyCode: string;
    externalClientId: string;
    guestCheckout: BooleanString | boolean;
    newUserWelcomeMessage: 'default';
    newUserWelcomeMessageDescription: 'default';
    returningUserWelcomeMessage: 'default';
    total: number | string;
    widget_style: WidgetStyle;
  };
  guest_checkout: boolean;
  incoming_traffic_source: string;
  merchant_profile_name: 'default';
  partner_name: string;
  rememberme_type: string;
  src_eligibility: string;
  user_flow?: string;
  user_type: UserType;
  username_remembered: boolean;
  vInitRequest: VInitRequest;
  vcopParams: {
    browser_protocol: 'http' | 'https';
    button_state: 'enabled';
    buttonType: string;
    cardArtSource: string;
    coBrandDisplayed: boolean;
    coBrandSupport: boolean;
    finger_print_auth_enabled: boolean;
    known_user: boolean;
    lastSelectedPay?: null;
    partnerName: string;
    stay_signed_in: string;
    vcopEnabledDevice: boolean;
  };
  vcop_enabled_device?: boolean;
};

export type SRCConfigs = {
  apikey?: string;
  buttonCardBrands?: CardNetwork[];
  campaignGuid?: string | null;
  cert?: SDKCert;
  channel: string;
  consumerNationalIdentifierRequested?: boolean;
  cookieCountry: CountryCode | null;
  cookieLocale: Locale | null;
  correlationId: string | null;
  countryCode?: CountryCode;
  countryCodeFromIp?: CountryCode | null;
  dataLayer?: object;
  dcfActionCode?: DCFActionCode | null;
  dpaData?: DpaData;
  dpaTransactionOptions?: DpaTransactionOptions;
  enabledFeatureToggles: FeatureToggles;
  eventSource: 'LightboxTW';
  formFactorMerchantOverride?: boolean;
  isCardFirstFlow?: boolean;
  isCookieConsentEnabled?: boolean;
  isERMCookieRefreshed?: boolean;
  isSSICookieRefreshed?: boolean;
  isInternalIp?: boolean;
  isInternalTraffic?: boolean;
  isSignedOut?: boolean;
  isSplunkLoggingEnabled?: boolean;
  isVsb?: boolean;
  isVsbButtonless?: boolean;
  locale?: Locale;
  merchantConfig: MerchantConfig;
  merchantName?: string;
  openvv: boolean;
  partialSelectedCard?: Partial<PaymentInstrument> | null;
  payMode?: boolean;
  paymentInstrumentType?: CardType;
  consumerNationalIdentifier?: string;
  policyVersion?: number;
  profileCountry?: CountryCode | null;
  profileLocale?: Locale | null;
  putIntentResponse?: FetchResponseValue<PurchaseIntentResponse> | null;
  routeBeforeLaunchDcf?: string;
  sdkCerts?: SDKCerts;
  sdkParams?: SDKParams;
  selectedCardId?: string;
  sessionId: string;
  showVDCPFlow?: boolean;
  showVdcpIno?: boolean;
  stepUp?: { grant: boolean };
  supportedCountries?: string;
  thmProfileHint: string | null;
  toRoute?: LaunchFlow;
  tokens?: {
    expiresIn?: number;
    tncEntryInfo?: TNCEntryInfo;
    xAuthCode?: string | null;
  };
  transfer?: {
    addCardInfo?: AddCardInfo;
  };
  userInfo: Partial<UserInfo>;
  uxByFormFactor?: UXByFormFactor | null;
  vInitRequest?: Partial<VInitRequest>;
  vIntentId?: string;
  visitId: string;
  xoFlow?: CheckoutFlow;
};

export type VSBIntegrationType = 'Button-VSB' | 'Integrated-VSB' | 'Standard button';

export type OrchestrationConfig = WithoutVoid<SRCConfigs> & {
  initializeVsbStartTime: number;
  isInteropEligible: boolean;
  formFactorMerchantOverride: boolean;
  isTokenizationEnabled: boolean;
  isVsbEnabled: boolean;
  isVsb: boolean;
  isVsbButtonless: boolean;
  merchantIntegrationType: VSBIntegrationType;
  vsbSDKLoadEnd: number;
  xoFlow: CheckoutFlow;
};

/* START GENAI@GHCOPILOT */
export enum AuthenticationDecision {
  BANK_APP = 'BANK_APP',
  CVV = 'CVV',
  FIDO_AUTH = 'FIDO_AUTH',
  FIDO_REG = 'FIDO_REG',
  GOOGLE_SPA = 'GOOGLE_SPA',
  ISSUER_CTF = 'ISSUER_CTF',
  ISSUER_OTP = 'ISSUER_OTP',
  THREE_DS = 'THREE_DS',
  VISA_OTP = 'VISA_OTP',
  VPP = 'VPP',
  VPP_AUTH = 'VPP_AUTH',
  VPP_BINDING = 'VPP_BINDING'
}

export enum WindowType {
  IFRAME = 'IFRAME',
  POPUP = 'POPUP'
}

export type AuthenticationStep = {
  authenticationDecision: AuthenticationDecision;
  windowType: WindowType;
};

export type PopUpService = {
  close: () => void;
  create: (...context: string[]) => Window | null | undefined;
  focus: () => void;
  get: () => Window | null | undefined;
  update: (...context: string[]) => Window | undefined;
};

/* END GENAI@GHCOPILOT */
